import { useRef, useState } from 'react'
import { Upload, Download, Trash2, FileText, Image, File } from 'lucide-react'
import type { LabAttachment } from '../../store/labOrdersSlice'

interface LabAttachmentsProps {
  attachments: LabAttachment[]
  labOrderId: string
  onUpload: (data: { lab_order_id: string; filename: string; url: string; file_type: string; size: number }) => Promise<void>
  onDelete: (attachmentId: string) => Promise<void>
}

function getFileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
    return <Image size={16} className="text-status-info" />
  }
  if (ext === 'pdf') {
    return <FileText size={16} className="text-status-error" />
  }
  if (['csv', 'xls', 'xlsx'].includes(ext)) {
    return <FileText size={16} className="text-status-success" />
  }
  return <File size={16} className="text-text-muted" />
}

export function LabAttachments({ attachments, labOrderId, onUpload, onDelete }: LabAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    for (const file of Array.from(files)) {
      const objectUrl = URL.createObjectURL(file)
      await onUpload({
        lab_order_id: labOrderId,
        filename: file.name,
        url: objectUrl,
        file_type: file.type,
        size: file.size,
      })
    }
    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDelete = async (id: string) => {
    await onDelete(id)
    setConfirmDeleteId(null)
  }

  return (
    <div data-testid="lab-attachments-section" className="bg-surface border border-border rounded-[6px]">
      <div className="flex items-center justify-between border-b border-border-light px-4 py-2.5">
        <h2 className="text-[14px] font-medium text-text-primary">Attachments</h2>
      </div>

      <div className="px-4 py-3 flex flex-col gap-3">
        {/* Upload area */}
        <div
          data-testid="file-upload-area"
          className="border-2 border-dashed border-border rounded-[6px] flex flex-col items-center justify-center py-6 hover:border-accent/40 transition-colors duration-100 cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={20} className="text-text-muted mb-2" />
          <span className="text-[12px] text-text-muted">
            {uploading ? 'Uploading...' : 'Click to upload lab report PDFs or images'}
          </span>
          <span className="text-[10px] text-text-disabled mt-1">
            PDF, JPG, PNG, CSV accepted
          </span>
          <input
            ref={fileInputRef}
            type="file"
            data-testid="file-input"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.gif,.csv,.xls,.xlsx"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Attachments list */}
        {attachments.length === 0 ? (
          <p className="text-[12px] text-text-muted text-center py-2" data-testid="no-attachments">
            No attachments
          </p>
        ) : (
          <div className="flex flex-col gap-1" data-testid="attachments-list">
            {attachments.map((att) => (
              <div
                key={att.id}
                data-testid={`attachment-${att.id}`}
                className="flex items-center gap-2 h-9 px-2 rounded-[4px] hover:bg-hover transition-colors duration-100"
              >
                {getFileIcon(att.filename)}
                <span className="text-[13px] text-text-primary flex-1 truncate" data-testid="attachment-filename">
                  {att.filename}
                </span>
                <a
                  href={att.url}
                  download={att.filename}
                  data-testid={`download-${att.id}`}
                  className="text-text-muted hover:text-accent cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download size={14} />
                </a>
                {confirmDeleteId === att.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`confirm-delete-${att.id}`}
                      onClick={() => handleDelete(att.id)}
                      className="h-5 px-1.5 rounded-[3px] bg-status-error text-white text-[10px] cursor-pointer"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="h-5 px-1.5 rounded-[3px] bg-border text-text-muted text-[10px] cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid={`delete-${att.id}`}
                    onClick={() => setConfirmDeleteId(att.id)}
                    className="text-text-muted hover:text-status-error cursor-pointer"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
